import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { ChevronLeft, KeyRound, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const AdminPasswordReset = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null); // user object
  const [newPwd, setNewPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {email, password}

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/admin/users');
        setUsers(Array.isArray(res.data) ? res.data : (res.data?.users || []));
      } catch (e) {
        console.error('Failed to load users', e);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const generatePassword = () => {
    // 12-char password: 1 upper, 1 lower, 1 digit, 1 symbol
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*';
    const all = upper + lower + digits + symbols;
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    let pwd = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
    for (let i = 0; i < 8; i++) pwd += pick(all);
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleReset = async () => {
    if (!selected) return;
    if (newPwd.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!window.confirm(`Reset password for ${selected.email}?\n\nThe user will need to use this new password to log in.`)) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/api/admin/users/set-password', {
        email: selected.email,
        new_password: newPwd,
      });
      if (res.data?.success) {
        setLastResult({ email: selected.email, password: newPwd, role: res.data.role });
        toast.success(`Password reset for ${selected.email}`);
        setNewPwd('');
        setSelected(null);
      } else {
        toast.error(res.data?.message || 'Reset failed');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Reset failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = users.filter(u => {
    const f = filter.trim().toLowerCase();
    if (!f) return true;
    return (
      (u.email || '').toLowerCase().includes(f) ||
      (u.name || '').toLowerCase().includes(f) ||
      (u.phone || '').toLowerCase().includes(f) ||
      (u.role || '').toLowerCase().includes(f)
    );
  }).sort((a, b) => {
    // surface barbers and admins first
    const order = { admin: 0, barber: 1, customer: 2 };
    const ra = order[a.role] ?? 9;
    const rb = order[b.role] ?? 9;
    if (ra !== rb) return ra - rb;
    return (a.name || a.email || '').localeCompare(b.name || b.email || '');
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-8" data-testid="admin-password-reset-page">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-white"
          onClick={() => navigate('/admin')}
          data-testid="back-to-dashboard-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <KeyRound className="w-7 h-7 text-amber-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Reset User Password</h1>
            <p className="text-zinc-400 text-sm mt-1">Set a new password for any user. Use this when a user is locked out or forgot their password.</p>
          </div>
        </div>

        {lastResult && (
          <Card className="bg-lime-500/10 border-lime-500/40 mb-6" data-testid="reset-success-card">
            <CardContent className="p-4 flex items-start gap-3">
              <Check className="w-6 h-6 text-lime-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-lime-300 font-semibold">Password reset successful</p>
                <p className="text-zinc-300 text-sm mt-1">
                  <span className="text-zinc-400">Email:</span> <span className="font-mono">{lastResult.email}</span>
                </p>
                <p className="text-zinc-300 text-sm">
                  <span className="text-zinc-400">New password:</span>{' '}
                  <span className="font-mono bg-zinc-900 px-2 py-1 rounded select-all">{lastResult.password}</span>
                </p>
                <p className="text-zinc-400 text-xs mt-2">📋 Send this to the user securely (text/in-person). They should change it after first login.</p>
              </div>
              <button onClick={() => setLastResult(null)} className="text-zinc-500 hover:text-white text-sm">Dismiss</button>
            </CardContent>
          </Card>
        )}

        {/* One-time fix: normalize all stored emails to lowercase */}
        <Card className="bg-blue-500/5 border-blue-500/30 mb-6">
          <CardContent className="p-4 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-blue-300 font-semibold text-sm">🔧 Fix case-sensitive login bug</p>
              <p className="text-zinc-400 text-xs mt-1">
                Run once to lowercase every stored email. Fixes users (like Keith) who can't log in
                after a password reset because their email was stored with capital letters.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 flex-shrink-0"
              onClick={async () => {
                if (!window.confirm('Lowercase every stored user email in the database?\n\nSafe to run multiple times. Will report any duplicates found.')) return;
                try {
                  const r = await api.post('/api/admin/users/normalize-emails');
                  const d = r.data;
                  toast.success(d.message || `Updated ${d.updated} emails.`);
                  if (d.conflicts && d.conflicts.length > 0) {
                    console.warn('Email conflicts:', d.conflicts);
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Migration failed');
                }
              }}
              data-testid="normalize-emails-btn"
            >
              Normalize all emails
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-lg">1. Find user</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Search by name, email, phone, or role…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-zinc-950 border-zinc-700 text-white"
              data-testid="user-search-input"
            />

            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading users…</div>
            ) : (
              <div className="mt-4 max-h-96 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800">
                {filtered.length === 0 ? (
                  <div className="p-6 text-center text-zinc-500">No users match.</div>
                ) : filtered.map(u => (
                  <button
                    key={u.id || u.email}
                    onClick={() => setSelected(u)}
                    className={`w-full text-left px-4 py-3 hover:bg-zinc-800 transition flex items-center justify-between ${
                      selected?.id === u.id ? 'bg-amber-500/10 ring-1 ring-amber-500/40' : ''
                    }`}
                    data-testid={`user-row-${u.email}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{u.name || '(no name)'}</p>
                      <p className="text-xs text-zinc-400 truncate">{u.email}</p>
                    </div>
                    <span className={`ml-3 text-xs px-2 py-0.5 rounded uppercase tracking-wider ${
                      u.role === 'admin' ? 'bg-red-500/20 text-red-300' :
                      u.role === 'barber' ? 'bg-lime-500/20 text-lime-300' :
                      'bg-zinc-700 text-zinc-300'
                    }`}>{u.role || 'customer'}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={`bg-zinc-900 border-zinc-800 ${!selected ? 'opacity-50' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg">2. Set new password</CardTitle>
          </CardHeader>
          <CardContent>
            {selected ? (
              <>
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-sm">
                    You're about to reset the password for{' '}
                    <span className="font-semibold">{selected.name || selected.email}</span> ({selected.email}).
                  </p>
                </div>

                <Label htmlFor="new-pwd" className="text-zinc-300">New password</Label>
                <div className="relative mt-1">
                  <Input
                    id="new-pwd"
                    type={showPwd ? 'text' : 'password'}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="At least 8 characters"
                    className="bg-zinc-950 border-zinc-700 text-white pr-20"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    data-testid="toggle-password-visibility-btn"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => { setNewPwd(generatePassword()); setShowPwd(true); }}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="generate-password-btn"
                  >
                    🎲 Generate strong password
                  </Button>
                </div>

                <div className="mt-6 flex gap-3">
                  <Button
                    onClick={handleReset}
                    disabled={submitting || newPwd.length < 8}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    data-testid="confirm-reset-btn"
                  >
                    {submitting ? 'Resetting…' : 'Reset Password'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setSelected(null); setNewPwd(''); }}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="cancel-reset-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Select a user above to set a new password.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPasswordReset;
